// Utilitaires pour la gestion correcte des URLs multilingues
// Évite les corruptions de type /fr + /blog = /frblog → /frog

import { getLanguagePathPrefix, resolveSupportedLanguage } from '../i18n';

/**
 * Détecte la langue actuelle depuis l'URL (code canonique, ex. fr-CH)
 */
export function getCurrentLanguage(): string | null {
  const raw = window.location.pathname.match(
    /^\/([a-zA-Z]{2}(?:-[a-zA-Z]{2})?)(?:\/|$)/i
  )?.[1];
  return resolveSupportedLanguage(raw);
}

/**
 * Construit une URL correcte en préservant la langue
 * @param path Le chemin de destination (ex: '/blog', '/admin', '/_console')
 * @param preserveLanguage Si true, préserve la langue actuelle
 * @returns L'URL complète correctement formatée
 */
export function buildUrl(path: string, preserveLanguage: boolean = true): string {
  if (!preserveLanguage) {
    return path;
  }

  const langPrefix = getLanguagePathPrefix();
  
  // Si le path est la racine
  if (path === '/') {
    return `${langPrefix}/`;
  }
  
  // Assurer que le path commence par /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  
  return `${langPrefix}${normalizedPath}`;
}

/**
 * Construit une URL pour un article de blog en préservant la langue
 */
export function buildBlogArticleUrl(articleId: string): string {
  return buildUrl(`/blog/${articleId}`);
}

/**
 * Construit une URL pour une page admin en préservant la langue
 */
export function buildAdminUrl(adminPath: string = ''): string {
  const basePath = '/_console';
  if (!adminPath) {
    return buildUrl(basePath);
  }
  
  const normalizedAdminPath = adminPath.startsWith('/') ? adminPath : `/${adminPath}`;
  return buildUrl(`${basePath}${normalizedAdminPath}`);
}

/**
 * Navigue vers une URL en préservant la langue
 */
export function navigateToUrl(path: string, preserveLanguage: boolean = true): void {
  const url = buildUrl(path, preserveLanguage);
  window.location.href = url;
}

/**
 * Navigue vers la page pricing avec un produit spécifique
 */
export function navigateToPricing(productId?: string): void {
  const basePath = '/pricing';
  const path = productId ? `${basePath}?product=${productId}` : basePath;
  navigateToUrl(path);
}

/**
 * Navigue vers la page contact
 */
export function navigateToContact(): void {
  navigateToUrl('/contact');
}

/**
 * Navigue vers la page d'accueil
 */
export function navigateToHome(): void {
  navigateToUrl('/');
}

/**
 * Navigue vers une page admin
 */
export function navigateToAdmin(adminPath: string = ''): void {
  const url = buildAdminUrl(adminPath);
  window.location.href = url;
}

/**
 * Navigue vers un article de blog
 */
export function navigateToBlogArticle(articleId: string): void {
  const url = buildBlogArticleUrl(articleId);
  window.location.href = url;
}
